import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
	type PointerEvent,
	type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Box,
	Button,
	CogOutline,
	Flex,
	HStack,
	IconButton,
	Link,
	MaximizeOutline,
	MenuAlt2Outline,
	MinimizeOutline,
	ShareOutline,
	Spinner,
	Text,
	Tooltip,
	useToast,
	XOutline,
} from '@bonusly/design-system';
import { Permission, useCurrentCompany, useCurrentUser, useFeatureFlag, useIsBonuslyCompany, usePermission } from '@bonusly/who-am-i';

import { useScrollAnchor } from '../../lib/bizy/connector';
import type { BizyUserConversationDetail } from '../../lib/bizy/types';
import { useBizyActivationState, useBizyFeedbackMutation, useBizyTitleMutation } from '../../queries/bizy';
import {
	bizyUserConversationQueryKey,
	bizyUserConversationsQueryKey,
	useBizyShareConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { Bizy, useBizyFlightAnchor, useIsBizyFlightAnchorHidden, type BizyState } from '../bizy';
import { DocumentRenderer } from '../bizy/renderers';
import { BizyAmbientGlow } from './bizy-ambient-glow';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { BizySidePanelSuggestedPrompts } from './bizy-side-panel-suggested-prompts';
import { BizyAdminEmptyState } from './components/bizy-admin-empty-state';
import { BizyWhatsNewTip } from './components/bizy-whats-new-tip';

import './lib/bizy-whats-new-tip-dev-helpers';

import { BizyComposer, type BizyComposerHandle } from './components/bizy-composer';
import { BizyFeedbackModal } from './components/bizy-feedback-modal';
import { BizyGameInvite } from './components/bizy-game-invite';
import { BizyInputBanner } from './components/bizy-input-banner';
import { BizyMessageFeedback } from './components/bizy-message-feedback';
import { BIZY_ALLOWED_MODELS, loadStoredBizyModel, saveStoredBizyModel } from './components/bizy-model-selector';
import { BizyNotesEditMessage } from './components/bizy-notes-edit-message';
import { BizyStreamingMessage } from './components/bizy-streaming-message';
import { BizyUserMessage } from './components/bizy-user-message';
import { useBizyExternalOpenAutoSend } from './hooks/use-bizy-external-open-auto-send';
import { useBizyGameEasterEgg } from './hooks/use-bizy-game-easter-egg';
import { useBizyGreeting } from './hooks/use-bizy-greeting';
import { useBizyPromptLibrary } from './hooks/use-bizy-prompt-library';
import { useBizySidePanelChat } from './hooks/use-bizy-side-panel-chat';
import { useBizyVoiceInput } from './hooks/use-bizy-voice-input';
import { buildPersonalizedGenerationKey, pickRandomPersonalizedChipPrompt, useBizyWhatsNewTip } from './hooks/use-bizy-whats-new-tip';
import { useLoadProactiveConversationOnOpen } from './hooks/use-load-proactive-conversation-on-open';
import { getActiveConversationTitle, getBizyConversationTitle } from './lib/conversation-title';
import { getCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { getToolProgressKey, isNotesEdit } from './lib/notes-edit-detection';
import { partnerNameFromPageName } from './lib/prompt-library-partner-name';
import { getAudienceSegment, getPromptChips, isTrialSegment, type ChipContext } from './lib/suggested-prompts';
import { useBuildBizyUiBlockActionDispatcher } from './lib/ui-block-action-dispatcher';
import { useBizyKitRendererProps } from './lib/use-bizy-kit-renderer-props';
import { useBizyActiveComposersStore } from './store/use-bizy-active-composers-store';
import { useBizyContextualInsertStore } from './store/use-bizy-contextual-insert-store';
import { useBizyExternalOpenStore } from './store/use-bizy-external-open-store';
import { useBizyPageContextStore } from './store/use-bizy-page-context-store';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

const RECENT_CONVERSATIONS_LIMIT = 5;
const LazyBizySwarmGame = lazy(() => import('./components/bizy-swarm-game').then((module) => ({ default: module.BizySwarmGame })));

interface PromptChipEntry {
	key: string;
	prompt: string;
	categoryId?: 'personalized';
}

interface BizySidePanelChatViewProps {
	onConversationIdChange?: (conversationId: string | undefined) => void;
	onOpenHistory: () => void;
	resetSignal?: number;
	showFullscreenToggle?: boolean;
}

export function BizySidePanelChatView({ onConversationIdChange, onOpenHistory, resetSignal = 0, showFullscreenToggle = true }: BizySidePanelChatViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const toast = useToast();
	const { mutate: submitFeedback } = useBizyFeedbackMutation();
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: shareConversation } = useBizyShareConversationMutation();
	const { mutate: generateTitle } = useBizyTitleMutation();
	const pageContext = useBizyPageContextStore((state) => state.context);
	const pageType = pageContext.pageType;
	const user = useCurrentUser();
	const isBizyModelExperimentEnabled = useFeatureFlag('bizy-model-experiment');
	const [selectedBizyModel, setSelectedBizyModel] = useState(() => loadStoredBizyModel(user.id));
	useEffect(() => {
		if (!isBizyModelExperimentEnabled) return;
		setSelectedBizyModel(loadStoredBizyModel(user.id));
	}, [isBizyModelExperimentEnabled, user.id]);
	const handleSelectBizyModel = useCallback(
		(model: string) => {
			if (!BIZY_ALLOWED_MODELS.has(model)) return;
			setSelectedBizyModel(model);
			saveStoredBizyModel(user.id, model);
		},
		[user.id]
	);

	const handleSuggestedPageClick = useCallback(
		(path: string) => {
			track(AnalyticsEvent.Bizy.SuggestedPageClicked, {
				pagePath: path,
				currentPath: window.location.pathname,
			});
		},
		[track]
	);
	const {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
		updateNodeChoice,
		loadConversation,
		isContinuous,
		didInitLoad,
		hasMoreHistory,
		isLoadingOlder,
		loadOlderTurns,
	} = useBizySidePanelChat({ model: isBizyModelExperimentEnabled ? selectedBizyModel : undefined });
	// Defined early so it's available when useBizyPromptLibrary is called below.
	const inputDisabled = isLoading || isOtherTabLoading;
	const queryClient = useQueryClient();
	const panelView = useBizySidePanelStore((state) => state.view);

	const composerRef = useRef<BizyComposerHandle>(null);
	// Stable wrapper so hooks/effects that capture setComposerText in a closure
	// always call through to the latest composerRef without triggering re-renders.
	const setComposerText = useCallback((text: string) => composerRef.current?.setInputText(text), []);

	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
	const [titleOverride, setTitleOverride] = useState<string | undefined>();
	const [isBizyHovered, setIsBizyHovered] = useState(false);
	const [isInputFocused, setIsInputFocused] = useState(false);
	const {
		isPromptOpen: isBizyGamePromptOpen,
		isGameOpen: isBizyGameOpen,
		holdProgress: bizyGameHoldProgress,
		isHolding: isBizyGameHolding,
		startBizyHold,
		cancelBizyHold,
		dismissPrompt: dismissBizyGamePrompt,
		openGame: openBizyGame,
		closeGame: closeBizyGame,
	} = useBizyGameEasterEgg();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const { containerRef: scrollAnchorRef, beforePrepend } = useScrollAnchor<HTMLDivElement>();
	const lastUserMsgRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	const isSmoothScrollingRef = useRef(false);
	const smoothScrollFrameRef = useRef<number | null>(null);
	const prevResetSignalRef = useRef(resetSignal);
	const hasTrackedBizyGameInviteShownRef = useRef(false);

	// Entry-point context cleared on "New chat" (see reset effect below).
	// `clearPendingContextualInsert` (store's `clearPendingContext`) is used by
	// the panel-close effect and UI block dispatcher; the New-chat reset path
	// deliberately does NOT use it (it clears via raw `setState` to skip
	// `onDismiss` — see the reset effect). `clearPendingRequest` has no side
	// effects, so the feature-request path can call it directly.
	const clearPendingContextualInsert = useBizyContextualInsertStore((state) => state.clearPendingContext);
	// Pollinator "Add on" / "Comment" buttons enter a contextual comment
	// drafting session (so the surface sticks across follow-up turns).
	const setPendingContextualInsert = useBizyContextualInsertStore((state) => state.setPendingContext);
	const clearPendingFeatureRequest = useBizyExternalOpenStore((state) => state.clearPendingRequest);

	const handleTranscript = useCallback((text: string) => {
		composerRef.current?.setInputText(text);
	}, []);
	const handleVoiceStart = useCallback(() => {
		track(AnalyticsEvent.Bizy.VoiceInputUsed, { path: window.location.pathname });
	}, [track]);
	const handleVoiceError = useCallback(
		(reason: string) => {
			track(AnalyticsEvent.Bizy.VoiceInputError, { reason, path: window.location.pathname });
		},
		[track]
	);
	const {
		isRecording: isVoiceRecording,
		isStarting: isVoiceStarting,
		startRecording: startVoice,
		stopRecording: stopVoice,
	} = useBizyVoiceInput({ onTranscript: handleTranscript, onStart: handleVoiceStart, onError: handleVoiceError });

	const cancelSmoothScroll = useCallback(() => {
		if (smoothScrollFrameRef.current !== null) {
			cancelAnimationFrame(smoothScrollFrameRef.current);
			smoothScrollFrameRef.current = null;
		}
		isSmoothScrollingRef.current = false;
	}, []);

	useEffect(() => {
		return cancelSmoothScroll;
	}, [cancelSmoothScroll]);

	useEffect(() => {
		if (resetSignal <= 0 || resetSignal === prevResetSignalRef.current) return;
		prevResetSignalRef.current = resetSignal;
		if (isVoiceRecording) void stopVoice();
		resetChat();
		composerRef.current?.setInputText('');
		setTitleOverride(undefined);
		// "New chat" must also drop any lingering entry-point context. Unlike
		// the panel-close path, starting a fresh chat keeps the panel open, so
		// without this the previous conversation's contextual `/` invocation
		// (source: contextual_recognition, bizy_contextual_*) or feature-request
		// source would leak onto the next, unrelated message's API context.
		//
		// Clear the contextual `/` context via raw `setState`, NOT
		// `clearPendingContext`: the latter fires `onDismiss`, which returns
		// focus (and smooth-scrolls) to the background give-box/comment input.
		// That's correct when the panel is dismissed, but on New chat the panel
		// stays open, so stealing focus to a hidden input would be a regression.
		if (useBizyContextualInsertStore.getState().pendingContext) {
			useBizyContextualInsertStore.setState({ pendingContext: null });
		}
		if (useBizyExternalOpenStore.getState().pendingRequest) clearPendingFeatureRequest();
	}, [isVoiceRecording, resetChat, resetSignal, stopVoice, clearPendingFeatureRequest]);

	const lastUserMsgIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') return i;
		}
		return -1;
	}, [messages]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		const inner = container?.firstElementChild as HTMLElement | null;
		if (panelView !== 'chat') {
			cancelSmoothScroll();
			return;
		}
		if (!container || !inner) return;

		if (shouldScrollRef.current) {
			shouldScrollRef.current = false;
			const el = lastUserMsgRef.current;
			if (el) {
				cancelSmoothScroll();
				inner.style.paddingBottom = `${container.clientHeight}px`;
				const scrollTarget = el.offsetTop - 36;
				// Smooth scroll to the last user message (we could just use smoothscroll but this allows us to set the speed)
				const startPos = container.scrollTop;
				const distance = scrollTarget - startPos;
				const duration = 400;
				let startTime: number | null = null;
				isSmoothScrollingRef.current = true;

				const step = (timestamp: number) => {
					if (!startTime) startTime = timestamp;
					const progress = Math.min((timestamp - startTime) / duration, 1);
					const eased = 1 - (1 - progress) ** 3;
					container.scrollTop = startPos + distance * eased;
					if (progress < 1) {
						smoothScrollFrameRef.current = requestAnimationFrame(step);
					} else {
						smoothScrollFrameRef.current = null;
						isSmoothScrollingRef.current = false;
					}
				};
				smoothScrollFrameRef.current = requestAnimationFrame(step);
			}
		}

		const currentPadding = parseFloat(inner.style.paddingBottom) || 0;
		if (currentPadding > 0 && !isSmoothScrollingRef.current) {
			const contentHeight = container.scrollHeight - currentPadding;
			const maxScrollWithoutPadding = contentHeight - container.clientHeight;
			const needed = Math.max(0, container.scrollTop - maxScrollWithoutPadding);
			inner.style.paddingBottom = `${needed}px`;
		}
	}, [messages, panelView, cancelSmoothScroll]);

	// After the continuous-mode init load completes, scroll to the bottom so the
	// user sees the most recent turn rather than the oldest.
	useEffect(() => {
		if (!isContinuous || !didInitLoad) return;
		requestAnimationFrame(() => {
			const container = scrollContainerRef.current;
			if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
		});
	}, [isContinuous, didInitLoad]);

	const { data: recentData } = useBizyUserConversations({ per_page: RECENT_CONVERSATIONS_LIMIT }, { enabled: !isContinuous });
	const recentConversations = useMemo(() => (recentData?.[0] ?? []).slice(0, RECENT_CONVERSATIONS_LIMIT), [recentData]);

	// Set title from server/cache when conversationId changes or when we get new data for the same conversation (e.g. page
	// refresh: effect ran before recentConversations loaded; when list loads, allow filling in server title). Skip only when
	// same conversation and no title to apply, so we don't overwrite with nothing after a refetch.
	const prevConversationIdForTitleRef = useRef<string | undefined>();
	useEffect(() => {
		if (!conversationId) return;
		const activeConversation = recentConversations.find((c) => c.conversationId === conversationId);
		const cached = activeConversation ? getBizyConversationTitle(activeConversation) : getCachedTitle(conversationId);
		const conversationChanged = prevConversationIdForTitleRef.current !== conversationId;
		if (conversationChanged) {
			prevConversationIdForTitleRef.current = conversationId;
		} else if (!cached) {
			// Same conversation, no new title data — don't overwrite (avoids reverting to wrong title on refetch)
			return;
		}
		setTitleOverride(cached || undefined);
	}, [conversationId, recentConversations]);

	// Keep the conversations list fresh so the dropdown includes the current chat
	const prevConversationIdRef = useRef<string | undefined>();
	useEffect(() => {
		if (conversationId && conversationId !== prevConversationIdRef.current) {
			prevConversationIdRef.current = conversationId;
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
		}
	}, [conversationId, queryClient]);

	// For proactive (Bizy-started) conversations, the user's first reply is
	// often terse ("ok", "thanks", 👋) and the LLM has nothing to summarize
	// — historically that produced placeholder titles like "Pending
	// Conversation Title". Bizy's opener carries the real topic, so we
	// derive the title from the FIRST message in the transcript regardless
	// of role: the user's prompt for user-initiated chats, Bizy's opener
	// for proactive ones.
	const { firstUserMessage, titleSourceMessage, hasFirstAssistant } = useMemo(() => {
		let firstUser: string | undefined;
		let titleSource: string | undefined;
		let hasAssistant = false;
		for (const m of messages) {
			if (firstUser === undefined && m.role === 'user') firstUser = m.content;
			if (titleSource === undefined && m.content && m.content.length > 0) titleSource = m.content;
			if (!hasAssistant && m.role === 'assistant' && m.id) hasAssistant = true;
			if (firstUser !== undefined && titleSource !== undefined && hasAssistant) break;
		}
		return { firstUserMessage: firstUser, titleSourceMessage: titleSource, hasFirstAssistant: hasAssistant };
	}, [messages]);
	const generatedTitle = getActiveConversationTitle(firstUserMessage);
	const conversationTitle = titleOverride ?? generatedTitle;

	// Generate AI title after the first assistant response completes.
	// Skip if the conversation already has a server-side or cached title.
	const hasRequestedTitleRef = useRef<string | undefined>();
	const manuallyRenamedRef = useRef(false);
	const conversationIdRef = useRef(conversationId);
	conversationIdRef.current = conversationId;

	useEffect(() => {
		if (!conversationId || !titleSourceMessage || titleOverride) return;
		if (hasRequestedTitleRef.current === conversationId) return;

		// We still gate on the first assistant reply existing so the LLM
		// has the substantive part of the exchange to draw from. For
		// proactive conversations that's also the title source; for
		// user-initiated ones it ensures we don't fire on a one-shot user
		// prompt that hasn't produced a response yet.
		if (!hasFirstAssistant) return;

		// Never overwrite a title the server already curated — e.g. a proactive
		// "dreaming" opener the backend titled "Something I noticed about you
		// and X".
		const activeConversationForTitle = recentConversations.find((c) => c.conversationId === conversationId);
		if (activeConversationForTitle?.conversationTitle) return;

		// A server-curated proactive title only reaches the client via the
		// recents list, so for a proactive opener (no user message yet) wait
		// until this conversation has loaded there before deriving a title from
		// Bizy's own opener — otherwise we'd race ahead and PATCH over the
		// server title. A freshly delivered proactive conversation is the
		// newest, so it lands in the recents window promptly; the trade-off is
		// that an older proactive opener reopened from history with no server
		// title keeps its time-of-day label until the user replies.
		if (firstUserMessage === undefined && !activeConversationForTitle) return;

		const requestConversationId = conversationId;
		hasRequestedTitleRef.current = conversationId;
		manuallyRenamedRef.current = false;
		generateTitle(
			{ message: titleSourceMessage },
			{
				onSuccess: (data) => {
					if (data.title && !manuallyRenamedRef.current && conversationIdRef.current === requestConversationId) {
						setTitleOverride(data.title);
						setCachedTitle(requestConversationId, data.title);
						updateConversation({ conversationId: requestConversationId, title: data.title });
						track(AnalyticsEvent.Bizy.ConversationTitleGenerated, { path: window.location.pathname });
					}
				},
			}
		);
	}, [conversationId, titleSourceMessage, titleOverride, hasFirstAssistant, firstUserMessage, recentConversations, generateTitle, updateConversation, track]);

	const company = useCurrentCompany();
	const isBonuslyCompany = useIsBonuslyCompany();
	const isCanceled = company.status === 'non_renewing';
	const isAdmin = user.admin;
	const displayName = user.preferred_first_name || user.first_name;
	// Admin-only Bizy: feature is enabled for admins but turned off for the rest
	// of the company. Only surface the notice to admins (who can change it).
	const featureStates = company.feature_states;
	const isBizyAdminOnly = Boolean(isAdmin && featureStates?.bizy_for_admins && featureStates?.bizy_for_employees === false);
	const segment = getAudienceSegment(company.status, isAdmin);
	const isTrial = isTrialSegment(segment);
	const { data: activationState } = useBizyActivationState(isTrial);
	const bizyForRewardsEnabled = useFeatureFlag('bizy-for-rewards');
	const chipContext: ChipContext | undefined = useMemo(
		() =>
			activationState && {
				...activationState,
				user: { ...activationState.user, earning_balance: user.earning_balance ?? 0 },
				bizy_for_rewards_enabled: bizyForRewardsEnabled,
			},
		[activationState, user.earning_balance, bizyForRewardsEnabled]
	);
	const canAccessUserManagement = usePermission(Permission.CanAccessUserManagement);
	const chipPromptName = partnerNameFromPageName(pageContext.pageName) ?? 'someone on my team';

	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const isHistoryDrawerOpen = useBizySidePanelStore((state) => state.isHistoryDrawerOpen);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);
	const lastTrigger = useBizySidePanelStore((state) => state.lastTrigger);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const showSettings = useBizySidePanelStore((state) => state.showSettings);

	// Prompt library: permissions, connector queries, favourites, model selector, handlers and modal.
	// Called here — before useBizyWhatsNewTip — because personalizedPromptSuggestions and
	// isPromptSuggestionsReady must be available to the tip hook below.
	const handlePromptLibrarySend = useCallback(
		(message: string, extraContext?: Record<string, string>) => {
			sendMessage(message, extraContext);
			shouldScrollRef.current = true;
			requestAnimationFrame(() => composerRef.current?.focusInput());
		},
		[sendMessage]
	);
	const handleAfterPromptSelect = useCallback(() => {
		shouldScrollRef.current = true;
		requestAnimationFrame(() => composerRef.current?.focusInput());
	}, []);
	const {
		footerContent: promptLibraryFooterContent,
		modal: promptLibraryModal,
		personalizedPromptSuggestions,
		isPromptSuggestionsReady,
	} = useBizyPromptLibrary({
		pageType,
		pageName: pageContext.pageName,
		isDisabled: inputDisabled,
		onSendMessage: handlePromptLibrarySend,
		onAfterSelect: handleAfterPromptSelect,
		onShowSettings: (tab) => showSettings(tab as Parameters<typeof showSettings>[0]),
		modelSelector: isBizyModelExperimentEnabled ? { selectedModel: selectedBizyModel, onSelectModel: handleSelectBizyModel } : undefined,
	});

	const pendingContextualInsert = useBizyContextualInsertStore((state) => state.pendingContext);
	const markPendingContextStarterSent = useBizyContextualInsertStore((state) => state.markPendingContextStarterSent);
	const triggerElementRef = useRef<HTMLElement | null>(null);
	const prevIsPanelOpenRef = useRef(isPanelOpen);
	const prevPanelViewRef = useRef(panelView);
	const prevIsLoadingConversationRef = useRef(isLoadingConversation);
	const isEmptyState = messages.length === 0 && !isLoading;
	const fullscreenFontSize = isFullscreen ? 'md' : undefined;
	const chatTextFontSize = isFullscreen ? 'md' : ({ base: 'md', sm: 'sm' } as const);

	useEffect(() => {
		onConversationIdChange?.(conversationId);
	}, [conversationId, onConversationIdChange]);

	// When the panel closes (any reason), drop any pending contextual `/` invocation
	// so the next open starts clean. `onDismiss` returns focus to the original input
	// (see use-bizy-contextual-insert-store).
	const prevIsPanelOpenForContextRef = useRef(isPanelOpen);
	useEffect(() => {
		const wasOpen = prevIsPanelOpenForContextRef.current;
		prevIsPanelOpenForContextRef.current = isPanelOpen;
		if (wasOpen && !isPanelOpen && pendingContextualInsert) {
			track(AnalyticsEvent.Bizy.ContextualDismissed, {
				surface: pendingContextualInsert.surface,
				path: window.location.pathname,
			});
			clearPendingContextualInsert();
		}
	}, [isPanelOpen, pendingContextualInsert, clearPendingContextualInsert, track]);

	// Auto-send the contextual starter on open so Bizy actively starts the
	// conversation (asks the right follow-up question) instead of waiting
	// silent. Dedup in the store, not in a component ref, so layout remounts
	// from resizing/fullscreen changes cannot replay the same contextual `/`.
	useEffect(() => {
		if (!pendingContextualInsert) return;
		if (!isPanelOpen) return;
		const currentContext = useBizyContextualInsertStore.getState().pendingContext;
		if (!currentContext || currentContext !== pendingContextualInsert || currentContext.hasSentStarter) return;
		markPendingContextStarterSent(currentContext);
		sendMessage(currentContext.starterMessage);
		shouldScrollRef.current = true;
	}, [pendingContextualInsert, isPanelOpen, markPendingContextStarterSent, sendMessage]);

	// Typed UI block action dispatcher. Passed to the shared
	// `DocumentRenderer` below via its `dispatchUiBlockAction` prop so that
	// any card renderer (recognition draft, comment draft, recognition view,
	// ...) can dispatch typed actions (`insert_into_draft`, `send_now`,
	// `open_in_givebox`, `view_post`, `dismiss`) without the chat view
	// having to thread surface-specific callbacks through every message.
	const navigate = useNavigate();
	// `getCommentComposer` reads from the active-composers store at
	// CALL time (not subscription time) so the dispatcher always sees
	// the latest mounted composer for a bonus, even if it remounted
	// after the dispatcher was built. `queuePendingCommentInsert` is
	// the fallback the dispatcher uses when no composer is mounted —
	// it stashes the formatted draft + the dispatcher then navigates
	// to /bonuses/{id}, where BonusContent auto-opens the editor and
	// the freshly-mounted CommentComposer drains the queue. Both reads
	// snapshot from `getState()` so the dispatcher's stable identity
	// doesn't depend on a Zustand subscription.
	const getCommentComposer = useBizyActiveComposersStore.getState().getCommentComposer;
	const queuePendingCommentInsert = useBizyActiveComposersStore.getState().queuePendingInsert;
	// Dispatcher-initiated sends (send_now / commit_draft / attach_gif …) must
	// scroll to the new turn just like a typed message does — the raw
	// sendMessage doesn't set shouldScrollRef, so wrap it here.
	const sendChatMessageWithScroll = useCallback(
		(content: string, extraContext?: Record<string, string>, displayOptions?: { attachmentPreviewUrl?: string }) => {
			sendMessage(content, extraContext, displayOptions);
			shouldScrollRef.current = true;
		},
		[sendMessage]
	);
	const uiBlockActionDispatcher = useBuildBizyUiBlockActionDispatcher({
		pendingContextualInsert,
		clearPendingContextualInsert,
		closePanel,
		navigate,
		currentConversationId: conversationId,
		getCommentComposer,
		queuePendingCommentInsert,
		sendChatMessage: sendChatMessageWithScroll,
		setPendingContext: setPendingContextualInsert,
	});

	// Environment-specific handlers the agnostic @bonusly/bizy-kit
	// DocumentRenderer delegates to (links, analytics, gif search, user
	// hydration, give-box hand-off). Built once and threaded into the renderer.
	const bizyKitRendererProps = useBizyKitRendererProps();

	const {
		pendingRequest: pendingFeatureRequest,
		showConflictDialog: showExternalOpenConflict,
		handleConfirmNewConversation,
		handleKeepCurrentConversation,
	} = useBizyExternalOpenAutoSend({
		isPanelOpen,
		isEmptyState,
		sendMessage,
		resetChat,
		shouldScrollRef,
		loadConversation,
		currentConversationId: conversationId,
		isLoadingConversation,
		setInputValue: setComposerText,
	});

	const conversationPathRef = useRef<string | null>(null);
	const [showPagePrompts, setShowPagePrompts] = useState(false);
	useEffect(() => {
		if (isEmptyState) {
			setShowPagePrompts(false);
			conversationPathRef.current = null;
			return;
		}
		if (conversationPathRef.current === null) {
			conversationPathRef.current = window.location.pathname;
		}
		setShowPagePrompts(window.location.pathname !== conversationPathRef.current);
	}, [isEmptyState, pageContext]);

	const prevConversationIdForPromptsRef = useRef(conversationId);
	useEffect(() => {
		const prev = prevConversationIdForPromptsRef.current;
		prevConversationIdForPromptsRef.current = conversationId;
		if (prev && conversationId && prev !== conversationId) {
			conversationPathRef.current = null;
			setShowPagePrompts(false);
		}
	}, [conversationId]);

	// The 1:1 list page no longer pops a Bizy self-introduction (BIZY-413): navigating to
	// the meet page must not auto-introduce Bizy to returning users. The page now falls
	// through to the standard empty state (greeting context + prompts) like every other
	// surface. We still track it so the admin first-launch intro stays suppressed here —
	// preserving prior behavior, where the meet intro took precedence on this page.
	const isOneOnOneListPage = pageType === '1on1_list';
	const isGreetingContextVisible = isEmptyState && isPanelOpen && panelView === 'chat';
	const { variant: greetingVariant, impressionCount } = useBizyGreeting(isGreetingContextVisible, isCanceled, isAdmin);
	const greetingKey = `greeting.${
		greetingVariant === 'canceled_admin' ? 'canceledAdmin' : greetingVariant === 'canceled_non_admin' ? 'canceledNonAdmin' : greetingVariant
	}`;
	const greetingText = t(greetingKey, { name: displayName });
	// Show the full admin capabilities block for the same impressions window as the
	// introductory greeting (0-3). After 4 impressions, admins fall through to the
	// familiar short greeting like everyone else.
	//
	// Gated to the admin roles whose work the intro copy actually speaks to (program
	// data, reports, financials, rewards, awards). Intentionally excludes Tech,
	// Announcements, and other role admins — `isAdmin` (Global-only) is still used
	// elsewhere in this file for the admin-only Bizy banner, canceled greeting
	// variant, prompt segment, and prompt chips, which should stay Global-scoped.
	//
	// `user.admin || user.global_admin` mirrors the canonical Global check in
	// `permission-validations.ts` `Permission.CanViewAdmin`. The two fields resolve
	// from the same backend source in `UserProfileBlueprint` but can diverge in
	// test fixtures and sample data, so we OR them defensively.
	const canSeeAdminIntro = user.admin || user.global_admin || user.finance_admin || user.rewards_admin || user.awards_admin || user.reports_admin;
	// Trial admins get the trial welcome empty state, not the admin first-launch
	// intro — its "make sense of your data / participation, trends" framing is the
	// same mature-program coaching we're keeping away from trials (see RR-102).
	const showAdminIntro = canSeeAdminIntro && !isTrial && !isOneOnOneListPage && greetingVariant === 'introductory';

	const hasTrackedGreetingRef = useRef(false);
	useEffect(() => {
		if (isGreetingContextVisible && !hasTrackedGreetingRef.current) {
			hasTrackedGreetingRef.current = true;
			track(AnalyticsEvent.Bizy.GreetingShown, {
				variant: greetingVariant,
				impressionCount,
				path: window.location.pathname,
			});
		}
		if (!isGreetingContextVisible) {
			hasTrackedGreetingRef.current = false;
		}
	}, [isGreetingContextVisible, greetingVariant, impressionCount, track]);

	// What's New tips (personalized for all users; static rotation for admins)
	const {
		tip: whatsNewTip,
		dismiss: dismissWhatsNewTip,
		shouldShowTip: showWhatsNewTip,
		hasActiveTip: hasActiveWhatsNewTip,
		dateKey: whatsNewDateKey,
	} = useBizyWhatsNewTip({
		canShowStaticTips: isAdmin,
		isEmptyState,
		isPanelOpen,
		personalizedPrompts: personalizedPromptSuggestions,
		personalizedPromptsResolved: isPromptSuggestionsReady,
		// Trial admins get the trial welcome + starter prompts instead of the
		// "What's new for admins" tips, which assume a mature program with data.
		isTrialCompany: isTrial,
	});

	const hasTrackedWhatsNewTipRef = useRef(false);
	useEffect(() => {
		if (showWhatsNewTip && whatsNewTip && !hasTrackedWhatsNewTipRef.current) {
			hasTrackedWhatsNewTipRef.current = true;
			track(AnalyticsEvent.Bizy.WhatsNewTipShown, {
				tipId: whatsNewTip.id,
				path: window.location.pathname,
			});
		}
		if (!showWhatsNewTip) {
			hasTrackedWhatsNewTipRef.current = false;
		}
	}, [showWhatsNewTip, whatsNewTip, track]);

	// Dismiss the What's New tip when the user engages with the chat by loading
	// a past conversation from history (or starting a new one). Gated on
	// `hasActiveWhatsNewTip` rather than `showWhatsNewTip` because loading a
	// past chat with messages sets `conversationId` and flips `isEmptyState`
	// false in the same render — `showWhatsNewTip` is already false by the
	// time this effect runs, but the underlying tip would otherwise re-appear
	// the next time the admin returns to the empty state today.
	const prevConversationIdForTipDismissRef = useRef(conversationId);
	useEffect(() => {
		const prev = prevConversationIdForTipDismissRef.current;
		prevConversationIdForTipDismissRef.current = conversationId;
		if (!prev && conversationId && hasActiveWhatsNewTip) {
			track(AnalyticsEvent.Bizy.WhatsNewTipDismissed, {
				tipId: whatsNewTip?.id,
				reason: 'conversation_loaded',
				path: window.location.pathname,
			});
			dismissWhatsNewTip();
		}
	}, [conversationId, hasActiveWhatsNewTip, whatsNewTip, dismissWhatsNewTip, track]);

	const personalizedChipSuggestion = useMemo(
		() => pickRandomPersonalizedChipPrompt(personalizedPromptSuggestions, buildPersonalizedGenerationKey(personalizedPromptSuggestions), whatsNewDateKey),
		[personalizedPromptSuggestions, whatsNewDateKey]
	);

	const promptChipEntries = useMemo<PromptChipEntry[]>(
		() => {
			const chipCount = isTrialSegment(segment) ? 5 : 3;
			const personalizedEntries = personalizedChipSuggestion
				? [
						{
							key: personalizedChipSuggestion.id,
							prompt: personalizedChipSuggestion.prompt,
							categoryId: 'personalized' as const,
						},
					]
				: [];
			const pageEntries = getPromptChips(pageType, segment, chipContext, isAdmin, Math.max(0, chipCount - personalizedEntries.length), pageContext.pageData, {
				canAccessUserManagement,
			}).map((key) => ({
				key,
				prompt: t(key, { name: chipPromptName }),
			}));

			return [...personalizedEntries, ...pageEntries];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pageType, resetSignal, segment, chipContext, isAdmin, chipPromptName, pageContext.pageData, canAccessUserManagement, personalizedChipSuggestion]
	);
	const promptChips = useMemo(
		() =>
			promptChipEntries.map((entry) => ({
				prompt: entry.prompt,
				expandable: entry.categoryId === 'personalized',
			})),
		[promptChipEntries]
	);
	const pageOnlyPromptChips = useMemo(
		() => promptChipEntries.filter((entry) => entry.categoryId !== 'personalized').map((entry) => ({ prompt: entry.prompt, expandable: false })),
		[promptChipEntries]
	);
	const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

	useEffect(() => {
		if ((!isPanelOpen || panelView !== 'chat') && (isVoiceRecording || isVoiceStarting)) {
			void stopVoice();
		}
	}, [isPanelOpen, panelView, isVoiceRecording, isVoiceStarting, stopVoice]);

	useEffect(() => {
		if (isPanelOpen && panelView === 'chat') return;

		cancelBizyHold();
		dismissBizyGamePrompt();
		closeBizyGame();
	}, [cancelBizyHold, closeBizyGame, dismissBizyGamePrompt, isPanelOpen, panelView]);

	useLayoutEffect(() => {
		const wasOpen = prevIsPanelOpenRef.current;
		prevIsPanelOpenRef.current = isPanelOpen;

		if (isPanelOpen && !wasOpen) {
			triggerElementRef.current = document.activeElement as HTMLElement | null;
			if ((lastTrigger === 'user' || lastTrigger === 'keyboard') && panelView === 'chat' && !isBizyGameOpen) {
				composerRef.current?.focusInput();
			}
		} else if (!isPanelOpen && wasOpen) {
			if (lastTrigger === 'user' || lastTrigger === 'keyboard') {
				triggerElementRef.current?.focus();
			}
			triggerElementRef.current = null;
		}
	}, [isBizyGameOpen, isPanelOpen, lastTrigger, panelView]);

	useEffect(() => {
		const returnedToChat = prevPanelViewRef.current !== 'chat' && panelView === 'chat';
		const finishedLoading = prevIsLoadingConversationRef.current && !isLoadingConversation;
		prevPanelViewRef.current = panelView;
		prevIsLoadingConversationRef.current = isLoadingConversation;

		if ((returnedToChat || finishedLoading) && isPanelOpen && !isBizyGameOpen) {
			requestAnimationFrame(() => {
				if (finishedLoading) {
					const container = scrollContainerRef.current;
					if (container) {
						container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
					}
				}
				composerRef.current?.focusInput();
			});
		}
	}, [panelView, isPanelOpen, isLoadingConversation, isBizyGameOpen]);

	const lastMessage = messages[messages.length - 1];
	const isStreaming = isLoading && lastMessage?.role === 'assistant' && !lastMessage.id;
	const showThinkingIndicator = isLoading && !isStreaming && lastMessage?.role !== 'assistant';
	const isBizyExcited = isBizyHovered || (isEmptyState && isInputFocused);
	// On peek-triggered opens, auto-load the conversation. For toggle / already-
	// open paths, leave it to the empty-state greeting to surface the new
	// message; user clicks the wave to engage.
	const { showGreeting, engageWithGreeting, acknowledgeUnread } = useLoadProactiveConversationOnOpen({
		loadConversation,
		// The coordinator uses this — not the react-query data cache — to
		// detect that the chat view actually swapped to the target
		// conversation before firing markSeen. Cached responses resolve
		// synchronously and would otherwise consume the notification
		// before the user could see Bizy's message.
		currentConversationId: conversationId,
		// On ANY open path with an unread (page refresh, preference-restore,
		// manual toggle, …) auto-load the proactive conversation. We used
		// to gate this on `isEmptyState` so an active prior conversation
		// stayed untouched, but a user loading into Bonusly with both a
		// previous conv and a new proactive notification would silently
		// land on the prior conv and have to find the new one in the
		// dropdown. Mid-session yank is prevented by:
		//   - the snapshot pattern (autoLoadOnOpen captured at open),
		//   - `passiveAutoLoadFiredThisOpenRef` (one-shot per session),
		//   - `engagementSuppressesPassiveRef` (any explicit engagement
		//     suppresses subsequent passive loads).
		autoLoadOnOpen: true,
	});
	const showNotificationGreeting = showGreeting;

	useEffect(() => {
		if (!showNotificationGreeting) return;
		dismissBizyGamePrompt();
		closeBizyGame();
	}, [closeBizyGame, dismissBizyGamePrompt, showNotificationGreeting]);

	useEffect(() => {
		if (!isBizyGamePromptOpen) return;

		const handlePointerDown = (event: globalThis.PointerEvent) => {
			if (event.target instanceof HTMLElement && event.target.closest('[data-bizy-game-invite]')) return;
			track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
				action: 'outside_dismiss',
				path: window.location.pathname,
			});
			dismissBizyGamePrompt();
		};

		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [dismissBizyGamePrompt, isBizyGamePromptOpen, track]);

	useEffect(() => {
		if (!isBizyGamePromptOpen || showNotificationGreeting) {
			hasTrackedBizyGameInviteShownRef.current = false;
			return;
		}
		if (hasTrackedBizyGameInviteShownRef.current) return;

		hasTrackedBizyGameInviteShownRef.current = true;
		track(AnalyticsEvent.Bizy.BizySkyHighInviteShown, {
			path: window.location.pathname,
		});
	}, [isBizyGamePromptOpen, showNotificationGreeting, track]);

	const handleBizyGameInvitePlay = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'play',
			path: window.location.pathname,
		});
		openBizyGame();
	}, [openBizyGame, track]);

	const handleBizyGameInviteDismiss = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'dismiss',
			path: window.location.pathname,
		});
		dismissBizyGamePrompt();
	}, [dismissBizyGamePrompt, track]);

	const handleBizyClick = useCallback(() => {
		if (showNotificationGreeting) {
			engageWithGreeting();
		}
	}, [engageWithGreeting, showNotificationGreeting]);

	const handleBizyPointerDown = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			if (showNotificationGreeting) return;
			event.currentTarget.setPointerCapture?.(event.pointerId);
			startBizyHold();
		},
		[showNotificationGreeting, startBizyHold]
	);

	const handleBizyPointerEnd = useCallback(() => {
		cancelBizyHold();
	}, [cancelBizyHold]);

	const handleBizyKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			if (showNotificationGreeting) {
				handleBizyClick();
				return;
			}
			if (event.repeat) return;
			startBizyHold();
		},
		[handleBizyClick, showNotificationGreeting, startBizyHold]
	);

	const handleBizyKeyUp = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			if (!showNotificationGreeting) cancelBizyHold();
		},
		[cancelBizyHold, showNotificationGreeting]
	);

	// When a proactive notification surfaces while the panel is already open
	// and the user is in a conversation, the in-chat Bizy waves + wiggles to
	// call attention to it. Hovering anywhere in the panel acknowledges the
	// attention so the animation stops (without firing markSeen — that only
	// happens when they click the Bizy to engage).
	//
	// We don't try to re-trigger the wiggle on a "second notification arrives
	// mid-session" boolean edge: the trigger pipeline cooldown spaces
	// notifications per user by days, so multiple notifications landing in a
	// single open session is not a real scenario.
	const [hasAcknowledgedAttention, setHasAcknowledgedAttention] = useState(false);

	const isCallingAttentionInChat = showNotificationGreeting && !hasAcknowledgedAttention && !isEmptyState;
	const bizyState: BizyState = isCallingAttentionInChat
		? 'waving'
		: isStreaming
			? 'buzzing'
			: isLoading
				? 'searching'
				: isBizyExcited
					? 'excited'
					: (bizyEmotion ?? 'curious');
	const bizyHoldSpinProgress = isBizyGameHolding ? Math.max(0, (bizyGameHoldProgress - 0.05) / 0.95) : 0;
	const bizyHoldSpinAnimation = { x: 0, y: 0, rotate: -360 * bizyHoldSpinProgress, scale: 1 };
	const bizyHoldSpinTransition = { duration: isBizyGameHolding ? 0.08 : 0.18, ease: 'linear' as const };
	// NOTE: the empty-state Bizy reuses `bizyState` directly. The
	// "doesn't wave for attention" semantics it documents are already
	// enforced by the `!isEmptyState` guard inside isCallingAttentionInChat
	// above — the navbar badge is the persistent signal, and any other
	// engagement entry point (clicking the Bizy, focusing the input,
	// clicking Ask Bizy) still fires markSeen.
	const emptyChatBizyAnchorRef = useBizyFlightAnchor('chat');
	const activeChatBizyAnchorRef = useBizyFlightAnchor('chat');
	const isChatBizyHidden = useIsBizyFlightAnchorHidden('chat');

	const thinkingPhrases = useMemo(() => {
		const raw = t('thinkingPhrases', { returnObjects: true });
		return Array.isArray(raw) ? (raw as string[]) : [t('thinking')];
	}, [t]);
	const [thinkingPhraseIdx, setThinkingPhraseIdx] = useState(() => Math.floor(Math.random() * thinkingPhrases.length));
	useEffect(() => {
		if (!isLoading || thinkingPhrases.length <= 1) {
			setThinkingPhraseIdx((prev) => {
				if (thinkingPhrases.length <= 1) return 0;
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
			return;
		}
		const id = setInterval(() => {
			setThinkingPhraseIdx((prev) => {
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
		}, 4000);
		return () => clearInterval(id);
	}, [isLoading, thinkingPhrases.length]);

	const toolProgressItems = activeToolCalls
		.map(getToolProgressKey)
		.filter((key): key is string => key !== null)
		.map((key) => t(key));

	const lastCompleteAssistantIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const isLastMsg = i === messages.length - 1;
			const isStreamingMsg = isLastMsg && isLoading && messages[i].role === 'assistant';
			if (messages[i].role === 'assistant' && messages[i].id && !isStreamingMsg) return i;
		}
		return -1;
	}, [messages, isLoading]);

	const handleVoiceToggle = useCallback(async () => {
		if (isVoiceRecording) {
			await stopVoice();
		} else {
			await startVoice();
		}
	}, [isVoiceRecording, startVoice, stopVoice]);

	const handleComposerSubmit = useCallback(
		(content: string) => {
			if (isVoiceRecording) void stopVoice();
			sendMessage(content);
			shouldScrollRef.current = true;
		},
		[isVoiceRecording, stopVoice, sendMessage]
	);

	const handleCancel = useCallback(() => {
		const restoredText = cancelGeneration();
		if (restoredText) {
			composerRef.current?.setInputText(restoredText);
			requestAnimationFrame(() => composerRef.current?.focusInput());
		}
	}, [cancelGeneration]);

	const handleSelectPrompt = useCallback(
		(prompt: string) => {
			const entry = promptChipEntries.find((e) => e.prompt === prompt);
			track(AnalyticsEvent.Bizy.SuggestionClicked, {
				suggestion_id: entry?.key,
				surface: pageType,
				is_trial: isTrial,
			});

			sendMessage(
				prompt,
				entry?.categoryId === 'personalized'
					? {
							prompt_source: 'suggested',
							prompt_key: entry.key,
							prompt_category: entry.categoryId,
						}
					: undefined
			);
			shouldScrollRef.current = true;
			requestAnimationFrame(() => composerRef.current?.focusInput());
		},
		[sendMessage, promptChipEntries, pageType, isTrial, track]
	);

	const conflictKeepRef = useRef<HTMLButtonElement>(null);

	const handlePositiveFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackPositive, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: 'positive' }, { onSuccess: () => updateMessageFeedback(messageId, 'positive') });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleNegativeFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackNegativeStarted, { path: window.location.pathname });
			setFeedbackMessageId(messageId);
			setIsFeedbackModalOpen(true);
		},
		[track]
	);

	const handleClearFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackCleared, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: null }, { onSuccess: () => updateMessageFeedback(messageId, undefined) });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleFeedbackModalSubmit = useCallback(
		(category: string, details?: string) => {
			if (!feedbackMessageId) return;
			track(AnalyticsEvent.Bizy.FeedbackNegativeSubmitted, { category, path: window.location.pathname });
			submitFeedback(
				{
					messageId: feedbackMessageId,
					feedback_type: 'negative',
					feedback_category: category,
					feedback_details: details,
				},
				{
					onSuccess: () => {
						updateMessageFeedback(feedbackMessageId, 'negative');
						setIsFeedbackModalOpen(false);
						setFeedbackMessageId(null);
					},
				}
			);
		},
		[feedbackMessageId, submitFeedback, updateMessageFeedback, track]
	);

	const handleShareToggle = useCallback(
		(id: string, shared: boolean) => {
			shareConversation(
				{ conversationId: id, shared },
				{
					onSuccess: () => {
						toast({
							title: shared ? t('sidePanel.chatSharedSuccess') : t('sidePanel.chatUnsharedSuccess'),
							status: 'success',
							duration: 3000,
						});
					},
					onError: () => {
						toast({ title: t('sidePanel.chatShareError'), status: 'error', duration: 3000 });
					},
				}
			);
		},
		[shareConversation, t, toast]
	);

	const activeConversation = recentConversations.find((c) => c.conversationId === conversationId);
	const activeConversationQueryKey = bizyUserConversationQueryKey(conversationId);
	const { data: cachedActiveConversation } = useQuery<BizyUserConversationDetail | undefined>({
		queryKey: activeConversationQueryKey,
		queryFn: () => Promise.resolve(queryClient.getQueryData<BizyUserConversationDetail>(activeConversationQueryKey)),
		enabled: false,
	});
	const activeSharedWithAdmin = cachedActiveConversation?.sharedWithAdmin ?? activeConversation?.sharedWithAdmin ?? false;

	const titleElement = (
		<HStack spacing={isHistoryDrawerOpen ? 0 : 1} minW={0}>
			{!isContinuous && !isHistoryDrawerOpen && (
				<Tooltip label={t('sidePanel.openBizyMenu')} hasArrow={false}>
					<IconButton
						aria-label={t('sidePanel.openBizyMenu')}
						icon={<MenuAlt2Outline boxSize={5} />}
						variant="ghost"
						size="sm"
						boxSize="32px"
						color="secondary.fg"
						_hover={{ bg: 'secondary.bgAlpha' }}
						onClick={onOpenHistory}
					/>
				</Tooltip>
			)}
			{!isContinuous && (
				<Text fontSize={isFullscreen ? 'lg' : 'md'} fontWeight="semibold" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{conversationTitle}
				</Text>
			)}
			{!isContinuous && activeSharedWithAdmin && (
				<Badge colorScheme="purple" size="sm" flexShrink={0}>
					{t('sidePanel.sharedBadge')}
				</Badge>
			)}
		</HStack>
	);

	return (
		<Flex
			direction="column"
			h="100%"
			position="relative"
			// Hovering anywhere in the panel acknowledges the attention
			// animation on the in-chat Bizy when a new proactive notification
			// has arrived. Click on the Bizy itself still engages.
			onMouseEnter={isCallingAttentionInChat ? () => setHasAcknowledgedAttention(true) : undefined}
		>
			<BizySidePanelHeader
				left={titleElement}
				pl={isFullscreen && isHistoryDrawerOpen ? 5 : 3}
				right={
					<HStack spacing={0}>
						{showFullscreenToggle && (
							<Tooltip label={isFullscreen ? t('sidePanel.exitFullscreen') : t('sidePanel.fullscreen')} hasArrow={false}>
								<IconButton
									aria-label={isFullscreen ? t('sidePanel.exitFullscreen') : t('sidePanel.fullscreen')}
									icon={isFullscreen ? <MinimizeOutline boxSize={4} /> : <MaximizeOutline boxSize={4} />}
									variant="ghost"
									size="sm"
									boxSize="32px"
									color="secondary.fg"
									_hover={{ bg: 'secondary.bgAlpha' }}
									onClick={toggleFullscreen}
								/>
							</Tooltip>
						)}
						{!isContinuous && isBonuslyCompany && conversationId && (
							<Tooltip label={activeSharedWithAdmin ? t('sidePanel.hideChatFromAdmins') : t('sidePanel.shareChatWithAdmins')} hasArrow={false}>
								<IconButton
									aria-label={activeSharedWithAdmin ? t('sidePanel.hideChatFromAdmins') : t('sidePanel.shareChatWithAdmins')}
									icon={<ShareOutline boxSize={5} />}
									variant="ghost"
									size="sm"
									boxSize="32px"
									color={activeSharedWithAdmin ? 'primary.fg' : 'secondary.fg'}
									_hover={{ bg: 'secondary.bgAlpha' }}
									onClick={() => handleShareToggle(conversationId, !activeSharedWithAdmin)}
								/>
							</Tooltip>
						)}
						{isContinuous && (
							<Tooltip label={t('sidePanel.settings')} hasArrow={false}>
								<IconButton
									aria-label={t('sidePanel.settings')}
									icon={<CogOutline boxSize={5} />}
									variant="ghost"
									size="sm"
									boxSize="32px"
									color="secondary.fg"
									_hover={{ bg: 'secondary.bgAlpha' }}
									onClick={() => showSettings()}
								/>
							</Tooltip>
						)}
						<Tooltip label={t('sidePanel.close')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.close')}
								icon={<XOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								boxSize="32px"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={() => closePanel()}
							/>
						</Tooltip>
					</HStack>
				}
			/>

			<Flex direction="column" flex={1} minH={0}>
				<Box position="relative" flex={1} overflow="hidden">
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						height="24px"
						background="linear-gradient(to bottom, var(--chakra-colors-bg-surface) 0%, transparent 100%)"
						pointerEvents="none"
						opacity={isScrolledFromTop ? 1 : 0}
						transition="opacity 0.15s ease-in-out"
						zIndex={1}
					/>
					<Box
						ref={(node) => {
							(scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
							(scrollAnchorRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
						}}
						data-bizy-chat-scroll
						height="100%"
						overflowY="auto"
						overscrollBehavior="contain"
						onScroll={(e: React.UIEvent<HTMLDivElement>) => {
							const { scrollTop } = e.currentTarget;
							setIsScrolledFromTop(scrollTop > 10);
							if (isContinuous && hasMoreHistory && !isLoadingOlder && scrollTop < 200) {
								void loadOlderTurns(beforePrepend);
							}
						}}
						sx={{ scrollPaddingTop: '36px' }}
						{...(isEmptyState ? { display: 'flex', flexDirection: 'column' as const } : {})}
					>
						<Flex
							direction="column"
							gap={2}
							px={4}
							pt={3}
							flex={isEmptyState ? 1 : undefined}
							minH={0}
							maxW={isFullscreen ? '744px' : undefined}
							mx={isFullscreen ? 'auto' : undefined}
							w="100%"
						>
							{isLoadingConversation && (
								<Flex direction="column" align="center" justify="center" flex={1} py={8} gap={3}>
									<Spinner size="lg" color="primary.fg" />
									<Text fontSize={fullscreenFontSize ?? 'sm'} color="text.soft">
										{t('sidePanel.loadingConversation')}
									</Text>
								</Flex>
							)}
							{!isLoadingConversation && isEmptyState && isPanelOpen && panelView === 'chat' && isPromptSuggestionsReady && (
								<>
									{showAdminIntro && !showWhatsNewTip && <BizyAdminEmptyState fontSize={fullscreenFontSize ?? 'sm'} />}

									{showWhatsNewTip && whatsNewTip ? (
										<Flex direction="column" align="center" justify="center" flex={1} py={6}>
											<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }}>
												<BizyWhatsNewTip
													tip={whatsNewTip}
													bizyAnchorRef={emptyChatBizyAnchorRef}
													isBizyHidden={isChatBizyHidden}
													onSelectPrompt={(prompt) => {
														track(AnalyticsEvent.Bizy.WhatsNewTipClicked, {
															tipId: whatsNewTip.id,
															path: window.location.pathname,
														});
														dismissWhatsNewTip();
														sendMessage(prompt);
														shouldScrollRef.current = true;
														requestAnimationFrame(() => composerRef.current?.focusInput());
													}}
												/>
											</motion.div>
										</Flex>
									) : (
										<Flex direction="column" align="center" justify="center" flex={1} py={6} gap={4}>
											<motion.div
												ref={emptyChatBizyAnchorRef}
												initial={{ scale: 0, opacity: 0 }}
												animate={{ scale: 1, opacity: 1 }}
												transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.25 }}
												onMouseEnter={() => setIsBizyHovered(true)}
												onMouseLeave={() => setIsBizyHovered(false)}
												style={{ visibility: isChatBizyHidden ? 'hidden' : 'visible', position: 'relative' }}
											>
												<motion.div animate={bizyHoldSpinAnimation} transition={bizyHoldSpinTransition} style={{ display: 'flex' }}>
													<motion.div
														// Clicking the empty-state Bizy with an unread engages
														// the proactive conversation. Otherwise a long press opens
														// the local game easter egg.
														onClick={handleBizyClick}
														onPointerDown={handleBizyPointerDown}
														onPointerUp={handleBizyPointerEnd}
														onPointerCancel={handleBizyPointerEnd}
														onKeyDown={handleBizyKeyDown}
														onKeyUp={handleBizyKeyUp}
														style={{ cursor: 'pointer', display: 'flex' }}
														role="button"
														tabIndex={0}
														aria-label={showNotificationGreeting ? t('sidePanel.openNewBizyMessage') : t('gameInvite.ariaLabel')}
													>
														<Bizy state={bizyState} size="md" />
													</motion.div>
												</motion.div>
												<AnimatePresence>
													{isBizyGamePromptOpen && !showNotificationGreeting && (
														<motion.div
															key="empty-bizy-game-invite"
															initial={{ opacity: 0, x: '-50%', y: 6, scale: 0.96 }}
															animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
															exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.96 }}
															transition={{ duration: 0.18 }}
															style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 10px)', zIndex: 2, pointerEvents: 'auto' }}
														>
															<BizyGameInvite onPlay={handleBizyGameInvitePlay} onDismiss={handleBizyGameInviteDismiss} />
														</motion.div>
													)}
												</AnimatePresence>
											</motion.div>

											{!showAdminIntro && !(isTrial && pageType === 'home') && (
												<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.45 }}>
													<Text fontSize="sm" color="text.soft" textAlign="center" maxW="260px" lineHeight="tall">
														{greetingText}
													</Text>
												</motion.div>
											)}
											{isTrial && pageType === 'home' && (
												<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.55 }}>
													<Flex direction="column" gap={1} align="center" maxW="280px">
														<Text fontSize="sm" fontWeight="semibold" color="text.default" textAlign="center">
															{t('trialWelcome.title', { name: displayName })}
														</Text>
														<Text fontSize="sm" color="text.soft" textAlign="center" lineHeight="tall">
															{t('trialWelcome.body')}
														</Text>
													</Flex>
												</motion.div>
											)}
										</Flex>
									)}
								</>
							)}
							{!isLoadingConversation &&
								messages.map((message, index) => {
									const isUser = message.role === 'user';
									const isLast = index === messages.length - 1;
									const isStreamingThisMessage = isLast && isStreaming;
									const isComplete = !isStreamingThisMessage && !isUser && !!message.id;

									if (isUser) {
										// Bubbles sent moments ago pop in with a springy bounce; older
										// bubbles (e.g. when loading a conversation's history) render
										// instantly so the whole transcript doesn't animate on mount.
										const isFreshUserMessage = message.timestamp ? Date.now() - new Date(message.timestamp).getTime() < 1500 : false;
										return (
											<BizyUserMessage
												key={message.key || index}
												ref={index === lastUserMsgIndex ? lastUserMsgRef : undefined}
												content={message.content}
												attachmentPreviewUrl={message.attachmentPreviewUrl}
												timestamp={message.timestamp}
												isFresh={isFreshUserMessage}
												fontSize={chatTextFontSize}
											/>
										);
									}

									if (!isStreamingThisMessage && isNotesEdit(message)) {
										return (
											<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
												<BizyNotesEditMessage content={message.content} StreamingMessage={BizyStreamingMessage} />
												{isComplete && message.id && (
													<BizyMessageFeedback
														messageId={message.id}
														content={message.content}
														feedbackType={message.feedbackType}
														onPositiveFeedback={handlePositiveFeedback}
														onNegativeFeedback={handleNegativeFeedback}
														onClearFeedback={handleClearFeedback}
														isLatestReply={index === lastCompleteAssistantIndex}
													/>
												)}
											</Flex>
										);
									}

									const messageToolProgress = isStreamingThisMessage ? toolProgressItems : [];
									const documentNodes = message.assistantMessage ?? [];

									return (
										<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
											{documentNodes.length > 0 ? (
												<DocumentRenderer
													{...bizyKitRendererProps}
													dispatchUiBlockAction={uiBlockActionDispatcher}
													fontSize={fullscreenFontSize}
													nodes={documentNodes}
													isComplete={!isStreamingThisMessage}
													messageKey={message.key}
													conversationId={conversationId}
													toolProgress={messageToolProgress}
													thinkingPhrase={thinkingPhrases[thinkingPhraseIdx]}
													nodeChoices={message.nodeChoices}
													onConfirm={(_cardIndex, confirmMessage) => {
														sendMessage(confirmMessage);
														shouldScrollRef.current = true;
													}}
													onChoiceUpdate={updateNodeChoice}
													onSuggestedPageClick={handleSuggestedPageClick}
													onSelectFollowUp={(suggestion) => {
														sendMessage(suggestion);
														shouldScrollRef.current = true;
													}}
													onDrill={(message) => {
														sendMessage(message);
														shouldScrollRef.current = true;
													}}
													followUpsDisabled={inputDisabled}
												/>
											) : (
												// Fallback for hydrated messages where assistantMessage is
												// absent (e.g. pre-backfill historical rows or proactive
												// notifications). Renders the plaintext bizy_response as
												// markdown. Live-streamed messages always populate
												// assistantMessage via the node-event handlers, so this
												// branch only fires on reload.
												<BizyStreamingMessage
													content={message.content}
													fontSize={fullscreenFontSize}
													isComplete={!isStreamingThisMessage}
													toolProgress={messageToolProgress}
													thinkingPhrase={thinkingPhrases[thinkingPhraseIdx]}
													onSuggestedPageClick={handleSuggestedPageClick}
													hideSources={isContinuous}
												/>
											)}
											{isComplete && message.id && (
												<BizyMessageFeedback
													messageId={message.id}
													content={message.content}
													feedbackType={message.feedbackType}
													onPositiveFeedback={handlePositiveFeedback}
													onNegativeFeedback={handleNegativeFeedback}
													onClearFeedback={handleClearFeedback}
													isLatestReply={index === lastCompleteAssistantIndex}
												/>
											)}
										</Flex>
									);
								})}

							{showThinkingIndicator && (
								<Box minH="20px" overflow="hidden" pl={1}>
									<AnimatePresence mode="wait" initial={false}>
										{toolProgressItems.length > 0 ? (
											toolProgressItems.map((item, i) => {
												const isLatest = i === toolProgressItems.length - 1;
												return (
													<motion.div
														key={`${i}-${item}`}
														initial={{ opacity: 0, y: 4 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -4 }}
														transition={{ duration: 0.35 }}
													>
														<Text
															fontSize="xs"
															fontWeight="medium"
															color="text.softest"
															sx={
																isLatest
																	? {
																			'@keyframes pulse': {
																				'0%, 100%': { opacity: 1 },
																				'50%': { opacity: 0.4 },
																			},
																			animation: 'pulse 1.5s ease-in-out infinite',
																		}
																	: { opacity: 0.6 }
															}
														>
															{item}
														</Text>
													</motion.div>
												);
											})
										) : (
											<motion.div
												key={thinkingPhraseIdx}
												initial={{ opacity: 0, y: 4 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -4 }}
												transition={{ duration: 0.35 }}
											>
												<Text
													fontSize="xs"
													fontWeight="medium"
													color="text.softest"
													sx={{
														'@keyframes pulse': {
															'0%, 100%': { opacity: 1 },
															'50%': { opacity: 0.4 },
														},
														animation: 'pulse 1.5s ease-in-out infinite',
													}}
												>
													{thinkingPhrases[thinkingPhraseIdx]}
												</Text>
											</motion.div>
										)}
									</AnimatePresence>
								</Box>
							)}

							{isTimedOut && (
								<Flex alignSelf="flex-start" direction="column" gap={1} pl={1}>
									<Text fontSize={fullscreenFontSize ?? 'sm'} color="text.softer">
										{t('timeout.message')}
									</Text>
									<Button variant="link" size="sm" color="text.interactive" onClick={retryLastMessage} px={0} justifyContent="flex-start">
										{t('timeout.retry')}
									</Button>
								</Flex>
							)}

							{messages.length > 0 && isPanelOpen && !isLoadingConversation && (
								<motion.div
									ref={activeChatBizyAnchorRef}
									layout
									transition={{ type: 'spring', stiffness: 200, damping: 25 }}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										padding: '8px 0',
										cursor: 'pointer',
										visibility: isChatBizyHidden ? 'hidden' : 'visible',
										position: 'relative',
									}}
									onMouseEnter={() => {
										setIsBizyHovered(true);
										// Also ack here, not just on the outer panel's
										// onMouseEnter. A notification arriving while the
										// cursor is already inside the panel (common — the
										// input is auto-focused, so the cursor often sits
										// near it) wouldn't otherwise trigger the panel-
										// boundary handler. Without this the wiggle would
										// continue indefinitely until the user left and
										// re-entered the panel.
										if (isCallingAttentionInChat) setHasAcknowledgedAttention(true);
									}}
									onMouseLeave={() => setIsBizyHovered(false)}
									// Bind click + a11y while there's an unread to engage with —
									// not while wiggling. The outer panel's onMouseEnter flips
									// hasAcknowledgedAttention true as soon as the cursor crosses
									// the panel boundary, so gating the click on
									// isCallingAttentionInChat would un-bind the handler before
									// the click on the Bizy could land. The wiggle animation
									// still depends on the ack flag so hover visually settles it.
								>
									<motion.div
										animate={isCallingAttentionInChat ? { x: [-12, 12, -12], y: [0, -6, 0], rotate: [-8, 8, -8] } : bizyHoldSpinAnimation}
										transition={isCallingAttentionInChat ? { duration: 0.7, ease: 'easeInOut', repeat: Infinity, repeatType: 'loop' } : bizyHoldSpinTransition}
										style={{ display: 'flex' }}
										onClick={handleBizyClick}
										onPointerDown={handleBizyPointerDown}
										onPointerUp={handleBizyPointerEnd}
										onPointerCancel={handleBizyPointerEnd}
										onKeyDown={handleBizyKeyDown}
										onKeyUp={handleBizyKeyUp}
										role="button"
										tabIndex={0}
										aria-label={showNotificationGreeting ? t('sidePanel.openNewBizyMessage') : t('gameInvite.ariaLabel')}
									>
										<Bizy state={bizyState} size="md" animations={isCallingAttentionInChat ? { wingSpeed: 0.12, wingAmplitude: 0.5 } : undefined} />
									</motion.div>
									<AnimatePresence>
										{isBizyGamePromptOpen && !showNotificationGreeting && (
											<motion.div
												key="active-bizy-game-invite"
												initial={{ opacity: 0, x: '-50%', y: 6, scale: 0.96 }}
												animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
												exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.96 }}
												transition={{ duration: 0.18 }}
												style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 10px)', zIndex: 2, pointerEvents: 'auto' }}
											>
												<BizyGameInvite onPlay={handleBizyGameInvitePlay} onDismiss={handleBizyGameInviteDismiss} />
											</motion.div>
										)}
									</AnimatePresence>
								</motion.div>
							)}
						</Flex>
					</Box>
				</Box>

				<Box
					px={isFullscreen ? 2 : 3}
					pb={2}
					maxW={isFullscreen ? '744px' : undefined}
					mx={isFullscreen ? 'auto' : undefined}
					w="100%"
					sx={{ paddingBottom: 'max(var(--chakra-space-3), env(safe-area-inset-bottom))' }}
				>
					{isEmptyState && !showWhatsNewTip && isPromptSuggestionsReady && (
						<BizySidePanelSuggestedPrompts fontSize={fullscreenFontSize} items={promptChips} onSelectPrompt={handleSelectPrompt} layout="vertical" />
					)}
					{!isEmptyState && showPagePrompts && !isLoading && (
						<BizySidePanelSuggestedPrompts fontSize={fullscreenFontSize} items={pageOnlyPromptChips} onSelectPrompt={handleSelectPrompt} layout="horizontal" />
					)}
					{isOtherTabLoading && (
						<Text fontSize="xs" color="text.softest" px={1} pb={1}>
							{t('otherTab.responding')}
						</Text>
					)}
					<Box position="relative">
						{isPanelOpen && <BizyAmbientGlow />}
						{isBizyAdminOnly && (
							<BizyInputBanner
								message={t('inputBanner.adminOnly.message')}
								action={
									<Link as={RouterLink} to="/admin/company/settings/ai" fontSize="sm" fontWeight="medium" color="text.interactive">
										{t('inputBanner.adminOnly.learnMore')}
									</Link>
								}
							/>
						)}
						<BizyComposer
							ref={composerRef}
							onSubmit={handleComposerSubmit}
							onCancel={handleCancel}
							isLoading={isLoading}
							disabled={isOtherTabLoading}
							placeholder={
								pendingContextualInsert
									? t(`contextualInvocation.inputPlaceholder.${pendingContextualInsert.surface}`)
									: pendingFeatureRequest?.promptHint || t('inputPlaceholder')
							}
							fontSize={chatTextFontSize}
							onVoiceToggle={handleVoiceToggle}
							isVoiceRecording={isVoiceRecording}
							isVoiceStarting={isVoiceStarting}
							onInputChange={(value) => {
								// Typing is direct engagement — mark any unread proactive notification
								// as seen without yanking the user out of their current conversation.
								acknowledgeUnread();
								// Dismiss the What's New tip when the user starts typing.
								if (showWhatsNewTip && value.trim()) {
									track(AnalyticsEvent.Bizy.WhatsNewTipDismissed, {
										tipId: whatsNewTip?.id,
										reason: 'typing',
										path: window.location.pathname,
									});
									dismissWhatsNewTip();
								}
							}}
							onFocus={() => setIsInputFocused(true)}
							onBlur={() => setIsInputFocused(false)}
							// Use onClick instead of onFocus — the panel auto-focuses the input
							// on open, and programmatic focus shouldn't consume an unread that
							// the user hasn't actually acknowledged yet.
							onClick={() => acknowledgeUnread()}
							footerLeft={promptLibraryFooterContent}
						/>
					</Box>
				</Box>
			</Flex>

			{promptLibraryModal}

			{isBizyGameOpen && !showNotificationGreeting && (
				<Suspense fallback={null}>
					<LazyBizySwarmGame onClose={closeBizyGame} />
				</Suspense>
			)}

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.Bizy.FeedbackNegativeCancelled, { path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMessageId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
			/>

			<AlertDialog
				leastDestructiveRef={conflictKeepRef as RefObject<HTMLButtonElement>}
				isOpen={showExternalOpenConflict}
				onClose={handleKeepCurrentConversation}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.newConversationTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.newConversationConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={conflictKeepRef} variant="outline" colorScheme="secondary" onClick={handleKeepCurrentConversation}>
								{t('sidePanel.keepCurrent')}
							</Button>
							<Button variant="solid" colorScheme="primary" onClick={handleConfirmNewConversation}>
								{t('sidePanel.startNew')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
